import { useCallback, useId, useMemo } from 'react';
import { type MutationKey, useQueryClient } from '@tanstack/react-query';

export interface ForgetRequest {
    /** Passed to the mutation, so `forget` finds this hook's requests and no other card's. */
    mutationKey: MutationKey;
    forget: () => void;
}

/**
 * A mutation's reset clears the hook's result but leaves the settled
 * mutation, request variables included, in the MutationCache until the hook unmounts. A
 * request carrying typed secrets must not outlive its outcome while the page stays open.
 */
export function useForgetRequest(name: string): ForgetRequest {
    const queryClient = useQueryClient();
    const id = useId();
    const mutationKey = useMemo(() => ['providerConfig', name, id], [name, id]);
    const forget = useCallback(() => {
        const cache = queryClient.getMutationCache();
        for (const mutation of cache.findAll({ mutationKey, exact: true }))
            if (mutation.state.status !== 'pending') cache.remove(mutation);
    }, [queryClient, mutationKey]);
    return { mutationKey, forget };
}
